"use client";
import { useMemo } from "react";
import { GrassScale, TreeScale, WeedScale } from "./constants";
import { cva } from "class-variance-authority";
import { useMediaQuery } from "@react-hook/media-query";
import parse from "html-react-parser";

// AQI Scale Data
const AQScale: any = [
  { label: "Green", color: "#39DEA4", level: "Good", value: "0 - 50" },
  { label: "Yellow", color: "#FED766", level: "Moderate", value: "51 - 100" },
  {
    label: "Orange",
    color: "#FF9C40",
    level: "Unhealthy for sensitive groups",
    value: "101 - 150",
  },
  { label: "Red", color: "#CE4030", level: "Unhealthy", value: "151 - 200" },
  {
    label: "Purple",
    color: "#4A327F",
    level: "Very unhealthy",
    value: "201 - 300",
  },
  { label: "Maroon", color: "#5F002B", level: "Hazardous", value: "301 - 500" },
];
const AQIThresholds = [
  {
    label: "Green",
    color: "#39DEA4",
    level: "Good",
    PM2_5: "0 - 12",
    PM10: "0 - 54",
    O3: "0 - 54",
    NO2: "0 - 53",
    SO2: "0 - 35",
    CO: "0 - 4.4",
  },
  {
    label: "Yellow",
    color: "#FED766",
    level: "Fine",
    PM2_5: "12.1 - 35.4",
    PM10: "55 - 154",
    O3: "55 - 70",
    NO2: "54 - 100",
    SO2: "36 - 75",
    CO: "4.5 - 9.4",
  },
  {
    label: "Orange",
    color: "#FF9C40",
    level: "Moderate",
    PM2_5: "35.5 - 55.4",
    PM10: "155 - 254",
    O3: "71 - 85",
    NO2: "101 - 360",
    SO2: "76 - 185",
    CO: "9.5 - 12.4",
  },
  {
    label: "Red",
    color: "#CE4030",
    level: "Poor",
    PM2_5: "55.5 - 250.4",
    PM10: "255 - 424",
    O3: "86 - 200",
    NO2: "361 - 1249",
    SO2: "186 - 604",
    CO: "12.5 - 30.4",
  },
  {
    label: "Purple",
    color: "#4A327F",
    level: "Very poor",
    PM2_5: "250.5 - 500.4",
    PM10: "425 - 604",
    O3: "201+",
    NO2: "1250 - 2049",
    SO2: "605 - 1004",
    CO: "30.5 - 50.4",
  },
  {
    label: "Maroon",
    color: "#5F002B",
    level: "Hazardous",
    PM2_5: "500.5+",
    PM10: "605+",
    O3: "-",
    NO2: "2050+",
    SO2: "1005+",
    CO: "50.5+",
  },
];

// Headers for each type
const headerTypes: any = {
  pollen: [
    "Color Scheme",
    "Risk Level",
    "Tree (ppm)",
    "Weed (ppm)",
    "Grass (ppm)",
  ],
  tree: ["Risk Level", "Tree (ppm)"],
  weed: ["Risk Level", "Weed (ppm)"],
  grass: ["Risk Level", "Grass (ppm)"],
  aq: ["Color Scheme", "Risk Level", "Index Values"],
  pollutants: [
    "Color Scheme",
    "Risk Level",
    "PM₂.₅ (µg/m³)",
    "PM<sub style='font-size: 0.65em;'>10</sub> (µg/m³)",
    "O<sub style='font-size: 0.65em;'>3</sub> (ppb)",
    "NO<sub style='font-size: 0.65em;'>2</sub> (ppb)",
    "SO<sub style='font-size: 0.65em;'>2</sub> (ppb)",
    "CO (ppm)",
  ],
};

// Row data for each type
const rowTypes: any = {
  pollen: TreeScale.map((item, index) => ({
    label: item.label,
    color: item.color,
    level: item.level,
    value: item.value,
    value2: WeedScale[index].value,
    value3: GrassScale[index].value,
  })),
  tree: TreeScale,
  weed: WeedScale,
  grass: GrassScale,
  aq: AQScale,
  pollutants: AQIThresholds.map((item, index) => ({
    label: item.label,
    color: item.color,
    level: item.level,
    value: item.PM2_5,
    value2: item.PM10,
    value3: item.O3,
    value4: item.NO2,
    value5: item.SO2,
    value6: item.CO,
  })),
};

// Styling
const cellStyle = cva([
  "py-2 px-[18px] text-neutral-light-500 border border-neutral-dark-400 font-inter whitespace-nowrap bg-neutral-dark-100",
]);

function ScaleTable({ category}: any) {
  const mobileView = useMediaQuery("(max-width: 1000px)");
  const headers = useMemo(() => headerTypes[category] || [], [category]);
  const rows = useMemo(() => rowTypes[category] || [], [category]);

  const renderColorCell = (color: string) => (
    <div
      style={{ backgroundColor: color }}
      className="p-1.5 h-3 rounded-full"
    ></div>
  );

  return (
    <div className="border-neutral-dark-400 shadow-md">
      {/* Scrollable wrapper for small screens */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left font-poppinsMedium  min-w-[600px]">
          <thead className="bg-neutral-dark-200 text-neutral-light-200 rounded-xl font-poppinsMedium text-sm">
            <tr>
              {headers.map((item: any, index: number) => (
                <th
                  key={index}
                  className={`whitespace-nowrap py-3 px-[18px] border border-neutral-dark-400 ${index === 0 ? "sticky left-0 bg-neutral-dark-200 z-10" : ""
                    }`}
                >
                  {parse(item)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item: any, index: number) => (
              <tr key={index}>
                {/* First Column - Static */}
                <td className={`sticky left-0 z-10 ${cellStyle()} flex space-x-4 items-center`}>
                  {renderColorCell(item.color)}
                  <p>{item.label}</p>
                </td>

                {/* Remaining Columns */}
                 <td className={cellStyle()}>{item.level}</td>
                <td className={`${cellStyle()}`}>{item.value}</td>

                {category === "pollen" ? (
                  <>
                    <td className={cellStyle()}>{item.value2}</td>
                    <td className={cellStyle()}>{item.value3}</td>
                  </>
                ) : (
                  category === "pollutants" &&
                   (
                    <>
                      <td className={cellStyle()}>{item.value2}</td>
                      <td className={cellStyle()}>{item.value3}</td>
                      <td className={cellStyle()}>{item.value4}</td>
                      <td className={cellStyle()}>{item.value5}</td>
                      <td className={cellStyle()}>{item.value6}</td>
                    </>
                  )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ScaleTable;
